/**
 * Language Test Card
 *
 * Animated card showing test completion state with icon and badge.
 *
 * Green means ONE thing: a level was read from the certificate, and the badge
 * shows it. There is no green badge without a level — the card used to fall
 * back to a green "Completed" for a failed analysis, which is the state the
 * citizen kept believing (H15).
 */

import { cn } from '@tonnie/foundation';
import { useTranslation } from '@tonnie/locale';
import { Badge, Card } from '@tonnie/ui';
import { CheckCircle2 } from '@tonnie/assets';
import { motion, AnimatePresence } from 'framer-motion';

import { type ILanguageTestConfig, cefrLevelLabel, getTestIcon } from './language-test.types';

export function LanguageTestCard({
  testConfig,
  assessedLevel,
  onOpen,
}: {
  testConfig: ILanguageTestConfig;
  /** The level the sub-test was assessed at; null while it has none. */
  assessedLevel: string | null;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  const IconComponent = getTestIcon(testConfig.id);
  const isCompleted = assessedLevel !== null;
  const levelName = assessedLevel !== null ? cefrLevelLabel(assessedLevel, t) : null;
  const levelLabel = levelName ? `${assessedLevel} — ${levelName}` : assessedLevel;

  return (
    <motion.div
      initial={false}
      animate={{ scale: isCompleted ? [1, 1.02, 1] : 1 }}
      transition={{ duration: 0.3 }}
    >
      <Card
        onClick={isCompleted ? undefined : onOpen}
        data-testid="widget-language_test-start"
        className={cn(
          'p-5 border-2 rounded-xl shadow-sm transition-all duration-300 relative overflow-hidden',
          isCompleted
            ? 'bg-linear-to-br from-success/15 to-success/5 border-success/50 cursor-default shadow-success/10'
            : 'bg-card border-border cursor-pointer hover:border-primary/50 hover:shadow-md hover:scale-[1.02] active:scale-[0.98]'
        )}
      >
        {isCompleted && (
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: '100%' }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="absolute top-0 left-0 h-1 bg-success"
          />
        )}

        <div className="flex justify-between items-start mb-3">
          <div
            className={cn(
              'p-2.5 rounded-lg transition-colors duration-300',
              isCompleted ? 'bg-success/20' : 'bg-muted'
            )}
          >
            <IconComponent
              className={cn(
                'w-6 h-6 transition-colors duration-300',
                isCompleted ? 'text-success-accessible' : 'text-muted-foreground'
              )}
            />
          </div>
          <AnimatePresence mode="wait">
            {isCompleted ? (
              <motion.div
                key="completed"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.2 }}
              >
                <Badge className="bg-success text-success-foreground border-success/30 text-xs font-semibold px-2.5 py-1">
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  <span data-testid="widget-language_test-score">{levelLabel}</span>
                </Badge>
              </motion.div>
            ) : (
              <motion.div
                key="pending"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <Badge variant="secondary" className="text-xs font-medium">
                  {t('checkup.languageTests.pending')}
                </Badge>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="mt-1">
          <span
            className={cn(
              'text-sm font-semibold transition-colors duration-300',
              isCompleted ? 'text-success-accessible' : 'text-foreground'
            )}
          >
            {testConfig.name}
          </span>
          {testConfig.description && (
            <p className="text-xs text-muted-foreground mt-1.5 line-clamp-2">
              {testConfig.description}
            </p>
          )}
        </div>
      </Card>
    </motion.div>
  );
}
